import { useState, useEffect, useCallback } from "react";
import {
  saveEventLog,
  getSessionId,
} from "../features/analytics/analyticsBatcher";
import {
  PageLocation,
  buildLocationUrl,
  logPageView,
} from "../services/logging";
import { LogProviderProps, LogContext } from "./log.context";

export const LogProvider: React.FC<LogProviderProps> = ({
  children,
}: LogProviderProps) => {
  const [, setUrl] = useState("");

  const handleFlush = useCallback((reason: string) => {
    saveEventLog(reason);
  }, []);

  useEffect(() => {
    // Always flush when the page is about to unload
    const handleUnload = () => {
      handleFlush("beforeunload");
    };

    // Flush every time the page is hidden. On mobile this is the last event a page reliably gets
    // (beforeunload often never fires), so the old "defer if flushed under a minute ago" timer was
    // killed with the tab and took the user's last actions with it. A flush with an empty queue
    // sends nothing, so tab switching costs at most one small keepalive POST.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        handleFlush("visibilitychange");
      }
    };

    // Set up a periodic flush for long-running sessions
    const intervalId = window.setInterval(() => {
      handleFlush("interval");
    }, 15 * 60 * 1000); // Every 15 minutes

    window.addEventListener("beforeunload", handleUnload);
    window.document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
      window.clearInterval(intervalId);

      handleUnload();
    };
  }, [handleFlush]);

  const logPage = useCallback((location: PageLocation) => {
    const u = buildLocationUrl(location);
    setUrl((eu) => {
      if (eu !== u) {
        logPageView(u);
      }
      return u;
    });
  }, []);

  useEffect(() => {
    logPage(window.location);
  }, [logPage]);

  return (
    <LogContext.Provider value={{ logPage, sessionId: getSessionId() }}>
      {children}
    </LogContext.Provider>
  );
};
